import type { CardPage, CardStockUpdate, CardSummary } from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  archiveCard,
  type CardListFilter,
  loadCard,
  loadCards,
  repriceCard,
  setCardFloor,
  setCardPrice,
  setCardStock,
} from "../../core/ops/cards-ops";
import { loadReviewQueue } from "../../core/ops/review-ops";
import { cardTitle } from "../../core/primitives/card-display";
import { errorText } from "../../core/primitives/errors";
import { Loader } from "../../core/primitives/loader";
import { displayedAvailable, stockValueCents } from "../../core/primitives/stock-list";
import { can } from "../../core/primitives/store-role";
import { PriceHistoryController } from "../../sections/price-history/price-history-controller";
import { type CardDialog, type CardPrices, CardsActions, CardsState } from "./cards-model";

export type CardsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "providersRepository" | "reviewQueueRepository"
>;

/** A notice says what just happened; it does not need dismissing by hand. */
export const NOTICE_MS = 4000;

/** How long taps on a row's stock settle before the count is sent. */
export const STOCK_SETTLE_MS = 900;

interface CardsQuery {
  readonly shopId: string;
  readonly filter: CardListFilter;
  readonly search: string;
  /** Set when fetching the page after the one shown: the answer appends rather than replaces. */
  readonly cursor?: string;
}

/**
 * The shop's catalogue — what it deals in and what each card is worth.
 *
 * Replaces the Inventory and Scans tabs. Those were two lenses on the scan;
 * this is the card, which is the thing. Notably it is the first screen that can
 * show the *whole* catalogue: Inventory was keyed on live offers, so a card
 * nobody had priced, an untracked card never listed, and a card whose offer had
 * sold were all invisible in it.
 */
export class CardsController extends SubtreeController implements CardsActions {
  readonly state = new CardsState();
  /** The price-history dialog: a section with its own model, registered into this page's subtree. */
  readonly history: PriceHistoryController;
  readonly #list: Loader<CardsQuery, CardPage>;
  #shopId: string | null = null;
  #noticeTimer: ReturnType<typeof setTimeout> | null = null;
  readonly #stockTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** The count last sent per card, until the card is re-read. */
  readonly #stockSent = new Map<string, number>();
  /** The save running per card, so a second waits for the first. */
  readonly #stockSaves = new Map<string, Promise<void>>();

  constructor(readonly deps: CardsDeps) {
    super();
    this.subtree.put(CardsState, this.state);
    this.subtree.put(CardsActions, this);
    this.history = this.own(new PriceHistoryController(this.subtree, deps));
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (query: CardsQuery) => loadCards({ ...deps, ...query }),
        loaded: (page, query) => {
          this.state.items.value = query.cursor ? [...this.state.items.value, ...page.cards] : page.cards;
          this.state.nextCursor.value = page.nextCursor;
        },
      }),
    );

    // The shell guarantees a shop before this page renders, but the selection can still change.
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      this.state.items.value = [];
      this.state.nextCursor.value = null;
      void this.#reload();
    }, [deps.storeRepository]);
    // Null role (not yet resolved) behaves like today — allowed — until it is known.
    this.sync(() => {
      const role = deps.storeRepository.selectedStore?.myRole ?? null;
      this.state.canEditInventory.value = role === null || can(role, "inventoryEdit");
    }, [deps.storeRepository]);
    this.sync(() => {
      const repo = deps.reviewQueueRepository;
      this.state.reviewCount.value = repo.groups.reduce((n, g) => n + g.count, 0);
      const shopId = deps.storeRepository.shopId;
      if (shopId !== null && (repo.status.kind === "idle" || repo.isStale))
        void loadReviewQueue({ ...deps, shopId, locationId: deps.storeRepository.locationId });
    }, [deps.reviewQueueRepository, deps.storeRepository]);
    this.sync(() => {
      this.state.stockValueCents.value = stockValueCents(
        this.state.items.value,
        this.state.stockEdits.value,
        this.state.nextCursor.value === null,
      );
    }, [this.state.items, this.state.stockEdits, this.state.nextCursor]);
    this.autoDispose(() => this.#clearNoticeTimer());
    // Leaving the page sends what was tapped rather than dropping it.
    this.autoDispose(() => this.#flushStock());
  }

  stepStock = (card: CardSummary, step: number) => {
    if (!card.trackStock || !this.state.canEditInventory.value) return;
    const edits = this.state.stockEdits.value;
    const shown = displayedAvailable(card, edits);
    const target = Math.max(0, shown + step);
    if (target === shown) return;
    const pending = this.#stockTimers.get(card.id);
    if (pending) clearTimeout(pending);
    this.#stockTimers.delete(card.id);
    // What the server holds or is about to: a save in flight counts, so stepping back during it is still sent.
    const held = this.#stockSent.get(card.id) ?? card.available;
    if (target === held) {
      if (this.#stockSent.has(card.id)) this.state.stockEdits.value = { ...edits, [card.id]: target };
      else {
        const { [card.id]: _dropped, ...rest } = edits;
        this.state.stockEdits.value = rest;
      }
      return;
    }
    this.state.stockEdits.value = { ...edits, [card.id]: target };
    this.#stockTimers.set(
      card.id,
      setTimeout(() => {
        this.#stockTimers.delete(card.id);
        void this.#commitStock(card.id);
      }, STOCK_SETTLE_MS),
    );
  };

  setFilter = (filter: CardListFilter) => {
    this.state.filter.value = filter;
    void this.#reload();
  };

  setSearch = (search: string) => {
    this.state.search.value = search;
    const query = this.#query();
    if (!query) return;
    // Typing waits for the word to settle; clearing the box answers at once.
    if (search) this.#list.loadDebounced(query);
    else void this.#list.load(query);
  };

  loadMore = async () => {
    const query = this.#query();
    const cursor = this.state.nextCursor.value;
    if (!query || !cursor) return;
    await this.#list.load({ ...query, cursor });
  };

  retry = () => {
    void this.#reload();
  };

  reprice = async (card: CardSummary) => {
    const shopId = this.#shopId;
    if (shopId === null || this.state.busyCardId.value !== null) return;
    this.state.busyCardId.value = card.id;
    this.state.notice.value = null;
    try {
      const r = await repriceCard({ ...this.deps, shopId, cardId: card.id });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.#notify(errorText(r.errors));
        return;
      }
      this.#notify(
        r.value == null ? `No price found for “${cardTitle(card)}”.` : `“${cardTitle(card)}” price refreshed.`,
      );
      await this.#reload();
    } finally {
      this.state.busyCardId.value = null;
    }
  };

  openSetPrice = (card: CardSummary) => this.#openDialog("price", card);
  openStock = (card: CardSummary) => this.#openDialog("stock", card);
  openArchive = (card: CardSummary) => this.#openDialog("archive", card);

  closeDialog = () => {
    this.state.dialog.value = null;
  };

  savePrices = async (next: CardPrices) => {
    // Two calls because they are two decisions with two endpoints: what the
    // shop asks, and what it will not go below.
    await this.#runDialog("price", async (shopId, card) => {
      const priced = await setCardPrice({ ...this.deps, shopId, cardId: card.id, priceCents: next.priceCents });
      if (!priced.ok) return priced;
      return setCardFloor({ ...this.deps, shopId, cardId: card.id, floorPriceCents: next.floorPriceCents });
    });
  };

  saveStock = async (change: CardStockUpdate) => {
    await this.#runDialog("stock", (shopId, card) => setCardStock({ ...this.deps, shopId, cardId: card.id, change }));
  };

  confirmArchive = async () => {
    await this.#runDialog("archive", (shopId, card) => archiveCard({ ...this.deps, shopId, cardId: card.id }));
  };

  dismissNotice = () => {
    this.#clearNoticeTimer();
    this.state.notice.value = null;
  };

  #openDialog(kind: CardDialog["kind"], card: CardSummary): void {
    this.state.dialogError.value = null;
    this.state.dialog.value = { kind, card };
  }

  /** Every card dialog behaves the same way: try, reload and close, or report in place and stay open. */
  async #runDialog(
    kind: CardDialog["kind"],
    action: (shopId: string, card: CardSummary) => Promise<Result<unknown, Error>>,
  ): Promise<void> {
    const dialog = this.state.dialog.value;
    const shopId = this.#shopId;
    if (!dialog || dialog.kind !== kind || shopId === null) return;
    this.state.busyCardId.value = dialog.card.id;
    this.state.dialogError.value = null;
    try {
      const r = await action(shopId, dialog.card);
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.dialogError.value = errorText(r.errors);
        return;
      }
      this.state.dialog.value = null;
      await this.#reload();
    } finally {
      this.state.busyCardId.value = null;
    }
  }

  /** Sends one card's count after any save still running for it, then re-reads just that card: the list keeps its pages. */
  #commitStock(cardId: string): Promise<void> {
    const previous = this.#stockSaves.get(cardId) ?? Promise.resolve();
    const save = previous.then(() => this.#saveStock(cardId));
    this.#stockSaves.set(cardId, save);
    void save.finally(() => {
      if (this.#stockSaves.get(cardId) === save) this.#stockSaves.delete(cardId);
    });
    return save;
  }

  async #saveStock(cardId: string): Promise<void> {
    const shopId = this.#shopId;
    const target = this.state.stockEdits.value[cardId];
    if (shopId === null || target === undefined || this.isDisposed) return;
    if (this.#stockSent.get(cardId) === target) return;
    this.#stockSent.set(cardId, target);
    const r = await setCardStock(this.#stockChange(shopId, cardId, target));
    if (this.isDisposed) return;
    if (!r.ok) this.#notify(errorText(r.errors));
    const detail = await loadCard({ ...this.deps, shopId, cardId });
    if (this.isDisposed) return;
    if (detail.ok) {
      const { card, offerId, channel, externalProductId, providerPriceCents } = detail.value;
      this.state.items.value = this.state.items.value.map((item) =>
        item.card.id === cardId ? { card, offerId, channel, externalProductId, providerPriceCents } : item,
      );
    }
    if (this.#stockSent.get(cardId) === target) this.#stockSent.delete(cardId);
    // A newer tap stays on screen until its own save lands.
    if (this.#stockTimers.has(cardId) || this.state.stockEdits.value[cardId] !== target) return;
    const { [cardId]: _sent, ...rest } = this.state.stockEdits.value;
    this.state.stockEdits.value = rest;
  }

  #stockChange(shopId: string, cardId: string, quantity: number) {
    return {
      ...this.deps,
      shopId,
      cardId,
      change: { quantity, kind: "ADJUSTED" as const, reason: "Counted on the stock list" },
    };
  }

  #flushStock(): void {
    const shopId = this.#shopId;
    for (const [cardId, timer] of this.#stockTimers) {
      clearTimeout(timer);
      const target = this.state.stockEdits.value[cardId];
      if (shopId !== null && target !== undefined) void setCardStock(this.#stockChange(shopId, cardId, target));
    }
    this.#stockTimers.clear();
  }

  #notify(text: string): void {
    this.#clearNoticeTimer();
    this.state.notice.value = text;
    this.#noticeTimer = setTimeout(() => {
      this.#noticeTimer = null;
      this.state.notice.value = null;
    }, NOTICE_MS);
  }

  #clearNoticeTimer(): void {
    if (this.#noticeTimer !== null) clearTimeout(this.#noticeTimer);
    this.#noticeTimer = null;
  }

  async #reload(): Promise<void> {
    const query = this.#query();
    if (query) await this.#list.load(query);
  }

  /** Null until the shop is known. */
  #query(): CardsQuery | null {
    if (this.#shopId === null) return null;
    return { shopId: this.#shopId, filter: this.state.filter.value, search: this.state.search.value };
  }
}
